"use client";

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Spinner } from "@/components/ui/spinner";
import { useTextInputFilter } from "@/hooks/use-text-input-filter";
import { useTopLoaderOnRefetch } from "@/hooks/use-top-loader-on-refetch";
import { trackCatalogResultsViewed, trackCatalogSearchSubmitted } from "@/lib/analytics";
import { SEARCH_PAGE_URL_CONFIG, yearRangeToParam } from "@/lib/faceted-search-url";
import { buildFilterLinkTarget } from "@/lib/filter-url-builder";
import { ALBUM_TYPES } from "@garimpa/lib/albumTypes";
import { FORMAT_CATEGORIES } from "@garimpa/lib/formatGroups";
import type { ReleaseSearchFilterKey } from "@garimpa/lib/searchState";

import { hasReleaseSearchIntent } from "./has-release-search-intent";
import { MobileFilterButton } from "./mobile-filter-button";
import { ResponsiveSearchFilters } from "./responsive-search-filters";
import { SearchActiveFilterChips } from "./search-active-filter-chips";
import type { SearchFiltersProps } from "./search-filters";
import { SearchInputHeader } from "./search-input-header";
import { searchPageFacetFilter } from "./search-page-facet-filter";
import { SearchResultsBody } from "./search-results-body";
import { useSearchMusicResults } from "./use-search-music-results";
import { useSearchSeoCopy } from "./use-search-seo-copy";
import { useSearchState } from "./use-search-state";

function musicResultCount(
  discovery: boolean,
  loading: boolean,
  facets: ReturnType<typeof useSearchMusicResults>["facets"],
): number | null {
  if (discovery || loading || !facets) return null;
  return facets.totalCount;
}

function SearchContent() {
  const searchState = useSearchState();
  const { filters, setFilter, setFilters, resetFilters, musicHasFilters, hasFilters } = searchState;
  const hasArtistInput = Boolean(filters.query.trim() || filters.genre || filters.style);
  const showDiscovery = !filters.query.trim() && !musicHasFilters;
  const preferAlbumsFirst = hasReleaseSearchIntent({
    type: filters.type,
    format: filters.format,
    country: filters.country,
    yearFrom: filters.yearFrom,
    yearTo: filters.yearTo,
    labels: filters.label,
    companies: filters.company,
  });
  const musicResults = useSearchMusicResults(filters, !showDiscovery, hasArtistInput);
  const seoCopy = useSearchSeoCopy(filters);
  const resultCount = musicResultCount(
    showDiscovery,
    musicResults.albumQuery.isLoading,
    musicResults.facets,
  );
  const isFetching = musicResults.isFetching;
  const isInitialLoading = musicResults.albumQuery.isLoading;
  useTopLoaderOnRefetch(isFetching && !isInitialLoading);

  const scrollToAlbums = useCallback(() => {
    const element = document.getElementById("albums-results");
    if (!element) return;
    const top = element.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: "smooth" });
  }, []);
  const updateFilter = useCallback(
    (
      key: string,
      value: string | number | boolean | undefined,
      options?: { replace?: boolean },
    ) => {
      setFilter(
        key as Parameters<typeof setFilter>[0],
        value as Parameters<typeof setFilter>[1],
        options,
      );
      if (key !== "q") scrollToAlbums();
    },
    [setFilter, scrollToAlbums],
  );
  const updateFilters = useCallback(
    (updates: Record<string, string | number | undefined>) => {
      setFilters(updates as Parameters<typeof setFilters>[0]);
      if (!("q" in updates)) scrollToAlbums();
    },
    [setFilters, scrollToAlbums],
  );
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchFilter = useTextInputFilter("q", filters.query, updateFilter, 400);
  const handleSearchSubmit = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      trackCatalogSearchSubmitted("search");
      searchInputRef.current?.blur();
      searchFilter.submit(searchFilter.value);
    },
    [searchState.activeFilterCount, searchFilter],
  );
  useEffect(() => {
    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    const requestedFocus = window.location.hash === "#focus";
    if (isDesktop || requestedFocus) {
      searchInputRef.current?.focus({ preventScroll: true });
      if (!isDesktop) requestAnimationFrame(() => window.scrollTo({ top: 0 }));
    }
    if (requestedFocus) {
      history.replaceState(null, "", window.location.pathname + window.location.search);
    }
  }, []);

  const currentFilterSearch = useMemo(
    () => ({
      q: filters.query || undefined,
      genre: filters.genre,
      style: filters.style,
      type: filters.type,
      format: filters.format,
      country: filters.country,
      origin: filters.origin,
      year: yearRangeToParam(filters.yearFrom, filters.yearTo),
      page: filters.page > 1 ? filters.page : undefined,
      label: filters.label.length ? filters.label : undefined,
      company: filters.company.length ? filters.company : undefined,
    }),
    [filters],
  );
  const trackedResultsKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (resultCount === null || isFetching) return;
    const key = JSON.stringify(currentFilterSearch);
    if (trackedResultsKeyRef.current === key) return;
    trackedResultsKeyRef.current = key;
    trackCatalogResultsViewed("search", resultCount);
  }, [currentFilterSearch, isFetching, resultCount]);
  const getFacetLinkTarget = useCallback(
    (key: ReleaseSearchFilterKey, value: string) =>
      buildFilterLinkTarget({
        to: "/search",
        currentSearch: currentFilterSearch,
        target: { config: SEARCH_PAGE_URL_CONFIG, key, mode: "toggle", value },
      }),
    [currentFilterSearch],
  );
  const facetFilter = useMemo(() => searchPageFacetFilter(filters), [filters]);
  const filterProps: SearchFiltersProps = {
    genre: filters.genre,
    style: filters.style,
    type: filters.type as (typeof ALBUM_TYPES)[number] | undefined,
    format: filters.format as (typeof FORMAT_CATEGORIES)[number] | undefined,
    country: filters.country,
    origin: filters.origin,
    yearFrom: filters.yearFrom,
    yearTo: filters.yearTo,
    label: filters.label,
    company: filters.company,
    filter: facetFilter,
    onLabelChange: (next) => {
      setFilter("label", next.length ? next : undefined);
      scrollToAlbums();
    },
    onCompanyChange: (next) => {
      setFilter("company", next.length ? next : undefined);
      scrollToAlbums();
    },
    facets: musicResults.facets,
    updateFilter,
    updateFilters,
    getFacetLinkTarget,
  };
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const mobileFilterTriggerRef = useRef<HTMLButtonElement>(null);
  const closeMobileFilters = useCallback(() => setMobileFiltersOpen(false), []);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <main className="max-w-[1440px] mx-auto flex gap-8 px-4 md:px-10 lg:px-20 py-8">
        {!showDiscovery && (
          <ResponsiveSearchFilters
            open={mobileFiltersOpen}
            filterProps={filterProps}
            hasFilters={hasFilters}
            resetFilters={resetFilters}
            onClose={closeMobileFilters}
            triggerRef={mobileFilterTriggerRef}
          />
        )}
        <div className="flex-1 min-w-0">
          <SearchInputHeader
            query={filters.query}
            inputRef={searchInputRef}
            inputValue={searchFilter.value}
            setInputValue={searchFilter.setValue}
            clearInput={searchFilter.clear}
            onSubmit={handleSearchSubmit}
            browseHeading={seoCopy.heading}
            resultCount={resultCount}
          />
          <SearchActiveFilterChips
            filters={filters}
            hasFilters={hasFilters}
            music
            updateFilter={updateFilter}
            updateFilters={updateFilters}
            setFilter={(key, value) => setFilter(key, value)}
          />
          <SearchResultsBody
            showDiscovery={showDiscovery}
            filters={filters}
            musicResults={musicResults}
            preferAlbumsFirst={preferAlbumsFirst}
            updateFilter={updateFilter}
            updateFilters={updateFilters}
          />
        </div>
      </main>
      <MobileFilterButton
        hidden={showDiscovery || mobileFiltersOpen}
        activeFilterCount={searchState.activeFilterCount}
        onOpen={() => setMobileFiltersOpen(true)}
        buttonRef={mobileFilterTriggerRef}
      />
    </div>
  );
}

export function SearchPageContent() {
  return (
    <Suspense fallback={<Spinner variant="page" />}>
      <SearchContent />
    </Suspense>
  );
}
